import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import type { MapFile } from "../game/mapfile";
import { hillTutorialSeen } from "../game/hill-tutorial";
import { MAP_CATALOG } from "../game/maps";
import { HILL_SITE } from "../game/site";
import { RIDE_CHOICES, RIDE_DETAILS, RIDE_LABELS } from "../game/settings";
import { storage } from "../game/storage";
import type { RideId } from "../game/sim/rides";
import { GHOST, PRIMARY, SECONDARY } from "./buttons";
import { FAKE_MAPS, FAKE_SKATERS } from "./long-lists.fixture";
import { PickList, type PickItem } from "./PickList";
import { PANEL, TITLE } from "./theme";
import { HILL, HillCard } from "./HillCard";
import { nearestBox, padEdges, type PadRead } from "./lobby-nav";
import { SkaterPicker } from "./SkaterSelect";
import { NewDot } from "./WhatsNew";

// The lobby (#174): before a map loads you pick who you are, where you skate
// and what you ride, then drop in. Bomb the Hill is the featured choice
// (#200): its card comes first with its own Drop in, and the parks are picked
// under Free skate. Start, Enter or any key drops into whichever was picked
// last on this screen, the hill until you pick a park. Every pick is remembered and stays
// changeable in Pause (Skater, Settings > Default ride, the map list). #225
// redid it as plain panels over the park (no backdrop scene, no flavour
// copy): who and what on the left, where on the right, and the middle left
// clear for the rider on the turntable. Round 2 made every pick a list
// (`PickList`), built for dozens of skaters and maps.
// The hill site (#219) is Bomb the Hill only: no Free skate, demo, 2-minute
// run or What's new, and every way in drops into the hill.

// Dev builds only, and loaded lazily so a production build never sees it
// (its checks run in a worker, which would be a second bundle).
const DevMapLoader = import.meta.env.DEV ? lazy(() => import("./DevMapLoader").then((m) => ({ default: m.DevMapLoader }))) : null;
// The flow lab's map forks (#191), only in a build made with VITE_MAP_LAB=1 (its preview).
const LabMaps = import.meta.env.VITE_MAP_LAB === "1" ? lazy(() => import("./LabMaps").then((m) => ({ default: m.LabMaps }))) : null;

const RIDE_ICONS: Record<RideId, string> = { board: "🛹", bmx: "🚲", scooter: "🛴", skates: "🛼" };

const rideItems: PickItem[] = RIDE_CHOICES.map((r) => ({
  id: r,
  name: RIDE_LABELS[r],
  title: RIDE_DETAILS[r],
  lead: (
    <span aria-hidden="true" className="w-6 shrink-0 text-center text-base">
      {RIDE_ICONS[r]}
    </span>
  ),
}));

// The dev fixture's dozens of fake skaters and maps (#225 round 2's
// screenshots): a dev build with `?long-lists` only; production drops it.
const LONG_LISTS = import.meta.env.DEV && typeof location !== "undefined" && new URLSearchParams(location.search).has("long-lists");

/** A ride's or map's picture beside its list, and its name: plain labels, no blurb (#225). */
function Preview({ art, name }: { art: ReactNode; name: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      {art}
      <p className={`${TITLE} text-base`}>{name}</p>
    </div>
  );
}

/** Polls the first pad while the lobby's up: the stick moves focus, ✕ presses, Start drops in. */
function usePadNav(root: React.RefObject<HTMLElement | null>, enabled: boolean, onGo: () => void): void {
  const go = useRef(onGo);
  go.current = onGo;
  useEffect(() => {
    if (!enabled) return;
    let prev: PadRead | null = null;
    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      let pad: Gamepad | null = null;
      try {
        pad = [...navigator.getGamepads()].find((p): p is Gamepad => !!p) ?? null;
      } catch {
        return; // a sandbox that refuses the Gamepad API
      }
      if (!pad) return;
      const now: PadRead = { buttons: pad.buttons.map((b) => b.pressed), axes: [...pad.axes] };
      // The first read only sets the baseline: the press that woke the pad up isn't a pick.
      const edges = prev ? padEdges(prev, now) : [];
      prev = now;
      const el = root.current;
      if (!el) return;
      for (const e of edges) {
        const buttons = [...el.querySelectorAll<HTMLElement>("button:not([disabled])")];
        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        if (e === "back") continue; // nothing to back out of in the lobby itself
        if (e === "go") go.current();
        else if (e === "press") (active && el.contains(active) ? active : buttons[0])?.click();
        else {
          const boxes = buttons.map((b) => {
            const r = b.getBoundingClientRect();
            return { x: r.left, y: r.top, w: r.width, h: r.height };
          });
          const next = nearestBox(boxes, active ? buttons.indexOf(active) : -1, e);
          buttons[next]?.focus();
        }
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [root, enabled]);
}

/** Any key drops in (a pad moves around instead), except Tab or a key picking a skater, ride or map. Off while something's open over the lobby (What's new, #185). */
function useAnyKey(enabled: boolean, onGo: () => void): void {
  const go = useRef(onGo);
  go.current = onGo;
  useEffect(() => {
    if (!enabled) return;
    const start = (e: KeyboardEvent) => {
      if (e.key === "Tab" || (e.target instanceof Element && e.target.closest("[data-map-picker], [data-skater-picker], [data-ride-picker]"))) return;
      go.current();
    };
    window.addEventListener("keydown", start);
    return () => window.removeEventListener("keydown", start);
  }, [enabled]);
}

/** The lobby: pick a skater, a ride and the hill or a park, then drop in; (in dev) load a map file, watch the demo or start a run. */
export function Lobby({
  touch,
  pad = false,
  covered = false,
  mapId,
  skaterId,
  rideId,
  onSkate,
  onPickSkater,
  onPickMap,
  onPickRide,
  onLoadFile,
  onHillTutorial,
  onWatchDemo,
  onStartRun,
  newNotes = false,
  onWhatsNew,
  nudge = null,
  onCloseNudge,
  hillOnly = HILL_SITE,
}: {
  touch: boolean;
  /** A pad is connected: poll it to move around the lobby. */
  pad?: boolean;
  /** Something's open over the lobby (What's new, #185): the pad drives that instead. */
  covered?: boolean;
  mapId: string;
  skaterId: string;
  rideId: RideId;
  onSkate: () => void;
  onPickSkater: (id: string) => void;
  onPickMap: (id: string) => void;
  onPickRide: (id: RideId) => void;
  onLoadFile: (file: MapFile) => void;
  /** Bomb the Hill's tutorial (#201) on the run about to start: the first time you drop in on the hill, or from its card. */
  onHillTutorial?: () => void;
  onWatchDemo: () => void;
  onStartRun: () => void;
  /** Release notes the player hasn't seen (#159): a dot on the What's new link. */
  newNotes?: boolean;
  onWhatsNew?: () => void;
  /** Until a pad has been seen (#161): suggest one, closable. */
  nudge?: string | null;
  onCloseNudge?: () => void;
  /** The hill site (#219): Bomb the Hill and nothing of the parks'. */
  hillOnly?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  // What Start, Enter or any key drops into: the featured hill until a park is picked under Free skate.
  const [pick, setPick] = useState<"hill" | "park">("hill");
  // The first time on the hill (#201), Drop in starts with the tutorial.
  const [firstHill] = useState(() => !hillTutorialSeen(storage()));
  const dropHill = (tutorial = firstHill) => {
    setPick("hill");
    onLoadFile(HILL);
    if (tutorial) onHillTutorial?.();
    onSkate();
  };
  const skatePark = () => {
    setPick("park");
    onSkate();
  };
  const go = pick === "hill" ? () => dropHill() : skatePark;
  usePadNav(root, pad && !covered, go);
  useAnyKey(!covered, go);
  const map = MAP_CATALOG.find((m) => m.id === mapId);
  const mapItems: PickItem[] = [
    ...MAP_CATALOG.map((m) => ({
      id: m.id,
      name: m.name,
      title: m.blurb,
      lead: m.thumb && <img src={m.thumb} alt="" width={24} height={24} className="size-6 shrink-0 rounded-sm border border-ink" />,
    })),
    ...(LONG_LISTS ? FAKE_MAPS : []),
  ];
  const hint = touch ? "Left thumb steers · right thumb does tricks" : pad ? "✕ picks · Start drops in · Select for controls" : "Any key drops in · H for controls";
  return (
    <div ref={root} data-lobby className="absolute inset-0 overflow-y-auto bg-ink/15 sm:bg-transparent sm:bg-linear-to-r sm:from-ink/30 sm:via-transparent sm:to-ink/30">
      <div className="relative mx-auto flex min-h-full max-w-6xl flex-col gap-4 px-4 pb-4 pt-16 sm:justify-center">
        {/* Wide screens and a phone on its side: who and what on the left, the hill and the parks on the right, the middle clear for the rider on the turntable. Narrow: one column, the hill first. */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex w-full flex-col gap-4 sm:order-last sm:w-[21rem] lg:w-[26rem]">
            <HillCard onDropIn={() => dropHill()} onTutorial={firstHill ? undefined : () => dropHill(true)} active={pick === "hill"} />
            {!hillOnly && (
              <PickList
                label="Free skate"
                data-map-picker
                items={mapItems}
                picked={mapId}
                onPick={(id) => {
                  if (!MAP_CATALOG.some((m) => m.id === id)) return;
                  setPick("park");
                  onPickMap(id);
                }}
                rows={4}
                aside={
                  map && (
                    <Preview
                      art={map.thumb && <img src={map.thumb} alt="" width={160} height={160} className="aspect-square w-full rounded-md border-2 border-ink" />}
                      name={map.name}
                    />
                  )
                }
                action={
                  <button type="button" data-park-skate onClick={skatePark} className={`${pick === "park" ? PRIMARY : SECONDARY} w-full px-4 py-2 text-sm`}>
                    Skate {map?.name ?? "the park"}
                  </button>
                }
              />
            )}
            {DevMapLoader && (
              <Suspense fallback={null}>
                <DevMapLoader onLoad={onLoadFile} />
              </Suspense>
            )}
            {LabMaps && (
              <Suspense fallback={null}>
                <LabMaps onLoad={onLoadFile} />
              </Suspense>
            )}
          </div>
          <div className="flex w-full flex-col gap-4 sm:w-[20rem] lg:w-[26rem]">
            <SkaterPicker skaterId={skaterId} onPick={onPickSkater} extra={LONG_LISTS ? FAKE_SKATERS : []} />
            <PickList
              label="Ride"
              data-ride-picker
              items={rideItems}
              picked={rideId}
              onPick={(id) => onPickRide(id as RideId)}
              rows={4}
              aside={
                <Preview
                  art={
                    <span aria-hidden="true" className="grid h-14 place-items-center rounded-md border-2 border-ink bg-surface-soft text-4xl">
                      {RIDE_ICONS[rideId]}
                    </span>
                  }
                  name={RIDE_LABELS[rideId]}
                />
              }
            />
          </div>
        </div>
        {nudge && (
          <p data-pad-nudge className={`${PANEL} mx-auto flex max-w-[min(90vw,30rem)] items-center gap-2 rounded-full py-1 pl-3 pr-1 text-xs font-bold`}>
            <span aria-hidden="true" className="text-base">
              🎮
            </span>
            <span className="min-w-0 flex-1">{nudge}</span>
            <button
              type="button"
              aria-label="Close controller tip"
              onClick={onCloseNudge}
              className="grid size-7 shrink-0 place-items-center rounded-full text-center text-sm font-black hover:bg-surface-soft focus-visible:outline-3 focus-visible:outline-focus"
            >
              ✕
            </button>
          </p>
        )}
        <div className="flex flex-col items-center gap-1">
          {!hillOnly && (
            <div className="flex flex-wrap justify-center gap-1">
              <button type="button" onClick={onWatchDemo} className={GHOST}>
                Watch demo
              </button>
              <button type="button" onClick={onStartRun} className={GHOST}>
                2-minute run
              </button>
              {onWhatsNew && (
                <button type="button" onClick={onWhatsNew} className={GHOST}>
                  What's new
                  {newNotes && <NewDot />}
                </button>
              )}
            </div>
          )}
          <p data-lobby-hint className="rounded-full bg-ink/60 px-3 py-0.5 text-center text-xs font-semibold text-white">
            {hint}
          </p>
        </div>
      </div>
    </div>
  );
}
